import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';

export interface ViewReportColumnConfigItem {
  key: string;
  label: string;
  order: number;
  visible?: boolean;
}

export interface ViewReportTableConfigPayload {
  name?: string;
  fieldConfigJson?: {
    data: ViewReportColumnConfigItem[];
  };
  isDefault?: boolean;
}

export interface ViewReportTableConfig {
  id?: number;
  name?: string;
  configName?: string;
  fieldConfigJson?: {
    data: ViewReportColumnConfigItem[];
  };
  data?: ViewReportColumnConfigItem[];
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdByUser?: {
    ID: number;
    FullName: string;
    EmailID: string;
  };
}

export interface ViewReportMeta {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ViewReportResponse {
  message?: string;
  data: any[];
  meta: ViewReportMeta;
}

function parseViewReportFieldConfigJson(
  fieldConfigJson: unknown,
): { data: ViewReportColumnConfigItem[] } | undefined {
  if (fieldConfigJson == null) return undefined;

  let parsed: unknown = fieldConfigJson;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return undefined;
    }
  }

  if (Array.isArray(parsed)) {
    return { data: parsed as ViewReportColumnConfigItem[] };
  }

  if (
    parsed &&
    typeof parsed === 'object' &&
    Array.isArray((parsed as { data?: unknown }).data)
  ) {
    return parsed as { data: ViewReportColumnConfigItem[] };
  }

  return undefined;
}

export function extractViewReportActiveConfigId(raw: unknown): number | undefined {
  if (!raw || typeof raw !== 'object') return undefined;

  const record = raw as Record<string, unknown>;
  if (typeof record.configId === 'number') return record.configId;
  if (typeof record.tableConfigId === 'number') return record.tableConfigId;

  if (record.config && typeof record.config === 'object') {
    const config = record.config as Record<string, unknown>;
    if (typeof config.id === 'number') return config.id;
  }

  if (typeof record.id === 'number') return record.id;
  return undefined;
}

export function normalizeViewReportTableConfig(
  raw: unknown,
): ViewReportTableConfig | null {
  if (!raw || typeof raw !== 'object') return null;

  const record = raw as Record<string, unknown>;
  const nested =
    record.config && typeof record.config === 'object'
      ? (record.config as ViewReportTableConfig)
      : (raw as ViewReportTableConfig);

  const fieldConfigJson =
    parseViewReportFieldConfigJson(nested.fieldConfigJson) ||
    (Array.isArray(nested.data) ? { data: nested.data } : undefined);

  if (!fieldConfigJson?.data?.length) {
    const id = extractViewReportActiveConfigId(raw);
    if (id) {
      return { ...nested, id };
    }
    return null;
  }

  return {
    ...nested,
    fieldConfigJson,
    data: nested.data ?? fieldConfigJson.data,
  };
}

export const getViewReportConfigColumns = (
  config?: ViewReportTableConfig | null,
): ViewReportColumnConfigItem[] => {
  if (!config) return [];

  const fieldConfig =
    parseViewReportFieldConfigJson(config.fieldConfigJson) ||
    (Array.isArray(config.data) ? { data: config.data } : undefined);

  return [...(fieldConfig?.data ?? [])]
    .filter(column => column.visible !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const viewReportApi = createApi({
  reducerPath: 'viewReport',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['ViewReport', 'ViewReportTableConfig'],
  endpoints: builder => ({
    getViewReport: builder.query<ViewReportResponse, any>({
      query: params => ({
        url: `/view-report${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['ViewReport'],
    }),

    exportViewReport: builder.query<Blob, any>({
      query: params => ({
        url: `/view-report/export${parseQueryParams(params || {})}`,
        method: 'GET',
        responseHandler: response => response.blob(),
      }),
    }),

    getViewReportTableConfigList: builder.query<
      BackendResponse<ViewReportTableConfig[]>,
      void
    >({
      query: () => ({
        url: '/view-report-table-config',
        method: 'GET',
      }),
      providesTags: ['ViewReportTableConfig'],
    }),

    getActiveViewReportTableConfig: builder.query<
      BackendResponse<ViewReportTableConfig>,
      void
    >({
      query: () => ({
        url: '/view-report-table-config/active',
        method: 'GET',
      }),
      providesTags: ['ViewReportTableConfig'],
    }),

    getViewReportTableConfig: builder.query<
      BackendResponse<ViewReportTableConfig>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/view-report-table-config/${id}`,
        method: 'GET',
      }),
      providesTags: (_, __, { id }) => [{ type: 'ViewReportTableConfig', id }],
    }),

    createViewReportTableConfig: builder.mutation<
      BackendResponse<ViewReportTableConfig>,
      Required<Pick<ViewReportTableConfigPayload, 'name' | 'fieldConfigJson'>> &
        Pick<ViewReportTableConfigPayload, 'isDefault'>
    >({
      query: payload => ({
        url: '/view-report-table-config',
        method: 'POST',
        body: payload,
      }),
    }),

    updateViewReportTableConfig: builder.mutation<
      BackendResponse<ViewReportTableConfig>,
      { id: number; data: ViewReportTableConfigPayload }
    >({
      query: ({ id, data }) => ({
        url: `/view-report-table-config/${id}`,
        method: 'PUT',
        body: data,
      }),
    }),

    deleteViewReportTableConfig: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/view-report-table-config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ViewReportTableConfig'],
    }),

    applyViewReportTableConfig: builder.mutation<
      BackendResponse<void>,
      { configId: number }
    >({
      query: payload => ({
        url: '/view-report-table-config/apply',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['ViewReportTableConfig'],
    }),
  }),
});

export const {
  useGetViewReportQuery,
  useLazyExportViewReportQuery,
  useGetViewReportTableConfigListQuery,
  useGetActiveViewReportTableConfigQuery,
  useGetViewReportTableConfigQuery,
  useLazyGetViewReportTableConfigQuery,
  useCreateViewReportTableConfigMutation,
  useUpdateViewReportTableConfigMutation,
  useDeleteViewReportTableConfigMutation,
  useApplyViewReportTableConfigMutation,
} = viewReportApi;

export default viewReportApi;
